import { Form, Formik } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";

import { OverlayLoading } from "components/common/OverlayLoading/OverlayLoading";
import { SimpleSubmitButton } from "components/common/SimpleSubmitBtn/SimpleSubmitButton";
import { PaymentTypeEnum } from "core/enum/payment-type.enum";
import { RouteEnum } from "core/enum/route.enum";
import { ShipmentMethodEnum } from "core/enum/shipment-method.enum";
import {
  TCartFormInitialValues,
  TCreateNewOrder,
} from "core/models/order.model";
import { useCreateNewOrder } from "core/services/api/order.api";
import { setRedirectUrl } from "core/services/auth/auth";
import { ErrorToast } from "core/utils/show-toast";
import { CartFormValidation } from "core/validations/cart-form-validation";
import { AppDispatch } from "store";
import { useSelectIsAuthenticated } from "store/auth/auth.selectors";
import {
  useCartItemsIdAndCount,
  useCartTotalPrice,
  useSelectPendingOrder,
} from "store/slices/cart/cart.selectors";
import {
  clearPendingOrder,
  setPendingOrder,
} from "store/slices/cart/cart.slice";
import { FormFields } from "./FormFields";

import walletIcon from "assets/img/wallet.svg";
import Style from "../Style.module.scss";

const CartForm: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelectIsAuthenticated();
  const pendingOrder = useSelectPendingOrder();

  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  const initialVaslues: TCartFormInitialValues = {
    paymentType: null,
    shipmentMethod: null,

    province: null,
    county: null,
    city: null,
    rural: null,
    village: null,
    postalCode: "",
    address: "",
  };

  const createNewOrder = useCreateNewOrder();

  const cartItems = useCartItemsIdAndCount();
  const totalPrice = useCartTotalPrice();

  const createOrder = (values: TCartFormInitialValues) => {
    const { paymentType, shipmentMethod, city, village, address, postalCode } =
      values;

    const cityOrVillageId = city?.value || village?.value;

    const isPost = shipmentMethod?.value === ShipmentMethodEnum.Post;

    const payload: TCreateNewOrder = {
      paymentTypeEnum: paymentType!.value,
      shipmentMethodEnum: shipmentMethod!.value,
      items: cartItems,

      ...(isPost && {
        cityOrVillageId,
        address,
        postalCode,
      }),
    };

    createNewOrder.mutate(payload, {
      onSuccess(response) {
        const code = response.data?.result;

        if (!code) {
          return ErrorToast(["کد پرداخت دریافت نشد"]);
        }

        setIsRedirecting(true);

        window.location.href = `https://mazagroombank.mazagroom.ir/bank/gotoipg?num=${code}`;
      },
    });
  };

  const onSubmit = (values: TCartFormInitialValues) => {
    if (!isAuthenticated) {
      dispatch(setPendingOrder(values));

      setRedirectUrl(history.location.pathname + history.location.search);

      history.push(RouteEnum.Login);

      return;
    }

    createOrder(values);
  };

  useEffect(() => {
    if (isAuthenticated && pendingOrder) {
      createOrder(pendingOrder);

      dispatch(clearPendingOrder());
    }
  }, [isAuthenticated, pendingOrder]);

  if (isRedirecting) return <OverlayLoading />;

  return (
    <div>
      <Formik
        initialValues={initialVaslues}
        onSubmit={onSubmit}
        validationSchema={CartFormValidation}
        enableReinitialize
      >
        {({ values }) => {
          const shouldDisableSubmit =
            values.paymentType == null ||
            values.shipmentMethod == null ||
            values.paymentType.value === PaymentTypeEnum.POS;

          return (
            <Form>
              <FormFields />

              <div className="mb-5">
                <div className="d-flex justify-content-center mt-0 mt-xl-4">
                  <img
                    src={walletIcon}
                    alt="wallet"
                    className={Style["wallet-img"]}
                  />
                </div>
                <div
                  className={`d-flex justify-content-xl-center justify-content-between mt-1 mt-xl-2 ${Style["balance"]}`}
                >
                  <span>مجموع خرید : </span>
                  <span>{totalPrice.toLocaleString()} ریال</span>
                </div>
              </div>
              <SimpleSubmitButton
                type="submit"
                btnText="پرداخت"
                disabled={shouldDisableSubmit}
                isLoading={createNewOrder.isLoading}
                className="mb-1 w-100 mt-1"
                style={{ backgroundColor: "var(--primary)", border: "none" }}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export { CartForm };
